<template>
  <DashboardLayout>
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">Maintenance Analytics</h1>
        <p class="text-sm text-gray-600 mt-1">Comprehensive reporting and insights</p>
      </div>

      <div class="flex gap-3">
        <!-- Date Range Filter -->
        <div class="flex items-center gap-2 bg-white px-4 py-2 rounded-lg shadow border border-gray-200">
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
          </svg>
          <input
            v-model="filters.start_date"
            type="date"
            class="border-none text-sm focus:ring-0 p-0"
            @change="loadReports"
          />
          <span class="text-gray-400">to</span>
          <input
            v-model="filters.end_date"
            type="date"
            class="border-none text-sm focus:ring-0 p-0"
            @change="loadReports"
          />
        </div>

        <!-- Export Button -->
        <button
          @click="exportReport"
          class="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"/>
          </svg>
          Export Report
        </button>
      </div>
    </div>

    <!-- Report Tabs -->
    <div class="bg-white rounded-lg shadow mb-6">
      <div class="border-b border-gray-200">
        <nav class="flex -mb-px">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id"
            :class="[
              'px-6 py-4 text-sm font-medium border-b-2 transition',
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            {{ tab.label }}
          </button>
        </nav>
      </div>
    </div>

    <!-- Asset Metrics Tab -->
    <div v-if="activeTab === 'assets'" class="space-y-6">
      <!-- Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Avg Utilization</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ assetMetrics.avg_utilization }}%
              </p>
            </div>
            <div class="p-3 bg-blue-100 rounded-lg">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Total Downtime</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ assetMetrics.total_downtime }} hrs
              </p>
            </div>
            <div class="p-3 bg-red-100 rounded-lg">
              <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Maintenance Cost</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                ${{ formatNumber(assetMetrics.total_maintenance_cost) }}
              </p>
            </div>
            <div class="p-3 bg-green-100 rounded-lg">
              <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">MTBF</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ assetMetrics.mtbf }} hrs
              </p>
              <p class="text-xs text-gray-500 mt-1">MTTR: {{ assetMetrics.mttr }} hrs</p>
            </div>
            <div class="p-3 bg-purple-100 rounded-lg">
              <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Asset Utilization Chart -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Asset Utilization</h3>
          <canvas ref="assetUtilizationChart"></canvas>
        </div>

        <!-- Downtime by Asset Chart -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Downtime by Asset</h3>
          <canvas ref="assetDowntimeChart"></canvas>
        </div>

        <!-- Maintenance Cost Trend -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Maintenance Cost Trend</h3>
          <canvas ref="costTrendChart"></canvas>
        </div>

        <!-- Top Assets by Cost -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Top 10 Assets by Cost</h3>
          <div class="space-y-3">
            <div
              v-for="asset in assetMetrics.top_assets_by_cost"
              :key="asset.id"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div class="flex items-center gap-3">
                <span class="text-sm font-medium text-gray-900">{{ asset.asset_tag }}</span>
                <span class="text-sm text-gray-600">{{ asset.name }}</span>
              </div>
              <span class="text-sm font-bold text-gray-900">${{ formatNumber(asset.total_cost) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Work Order Metrics Tab -->
    <div v-if="activeTab === 'work_orders'" class="space-y-6">
      <!-- Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Completion Rate</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ workOrderMetrics.completion_rate }}%
              </p>
            </div>
            <div class="p-3 bg-green-100 rounded-lg">
              <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Avg Completion Time</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ workOrderMetrics.avg_completion_time }} hrs
              </p>
            </div>
            <div class="p-3 bg-blue-100 rounded-lg">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Backlog</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ workOrderMetrics.backlog_count }}
              </p>
              <p class="text-xs text-gray-500 mt-1">{{ workOrderMetrics.overdue_count }} overdue</p>
            </div>
            <div class="p-3 bg-yellow-100 rounded-lg">
              <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Total Cost</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                ${{ formatNumber(workOrderMetrics.total_cost) }}
              </p>
            </div>
            <div class="p-3 bg-purple-100 rounded-lg">
              <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- WO Status Distribution -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Status Distribution</h3>
          <canvas ref="woStatusChart"></canvas>
        </div>

        <!-- WO Type Distribution -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Type Distribution</h3>
          <canvas ref="woTypeChart"></canvas>
        </div>

        <!-- Completion Trend -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Completion Trend</h3>
          <canvas ref="woCompletionTrendChart"></canvas>
        </div>

        <!-- Priority Breakdown -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Priority Breakdown</h3>
          <canvas ref="woPriorityChart"></canvas>
        </div>
      </div>
    </div>

    <!-- PM Compliance Tab -->
    <div v-if="activeTab === 'pm_compliance'" class="space-y-6">
      <!-- Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Compliance Rate</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ pmMetrics.compliance_rate }}%
              </p>
            </div>
            <div class="p-3 bg-green-100 rounded-lg">
              <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Scheduled PM</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ pmMetrics.scheduled_count }}
              </p>
            </div>
            <div class="p-3 bg-blue-100 rounded-lg">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Completed PM</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ pmMetrics.completed_count }}
              </p>
            </div>
            <div class="p-3 bg-green-100 rounded-lg">
              <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Overdue PM</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ pmMetrics.overdue_count }}
              </p>
            </div>
            <div class="p-3 bg-red-100 rounded-lg">
              <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts and Tables -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Compliance Trend -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Compliance Trend</h3>
          <canvas ref="pmComplianceChart"></canvas>
        </div>

        <!-- PM Effectiveness -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">PM Effectiveness</h3>
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-600">Preventive WOs</span>
              <span class="text-sm font-bold text-green-600">{{ pmMetrics.preventive_wo_count }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-600">Reactive WOs</span>
              <span class="text-sm font-bold text-red-600">{{ pmMetrics.reactive_wo_count }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-gray-600">PM/Reactive Ratio</span>
              <span class="text-sm font-bold text-gray-900">{{ pmMetrics.pm_reactive_ratio }}</span>
            </div>
            <div class="pt-4 border-t">
              <p class="text-xs text-gray-500 mb-2">Effectiveness Score</p>
              <div class="w-full bg-gray-200 rounded-full h-2">
                <div
                  :style="{ width: pmMetrics.effectiveness_score + '%' }"
                  class="bg-green-600 h-2 rounded-full"
                ></div>
              </div>
              <p class="text-right text-sm font-bold text-gray-900 mt-1">{{ pmMetrics.effectiveness_score }}%</p>
            </div>
          </div>
        </div>

        <!-- PM Plans List -->
        <div class="bg-white rounded-lg shadow p-6 md:col-span-2">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">PM Plans Compliance</h3>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan Name</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assets</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scheduled</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Overdue</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Compliance</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="plan in pmMetrics.plans" :key="plan.id">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ plan.name }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ plan.asset_count }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ plan.scheduled }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ plan.completed }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-red-600">{{ plan.overdue }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div class="flex items-center gap-2">
                      <div class="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          :style="{ width: plan.compliance_rate + '%' }"
                          :class="[
                            'h-2 rounded-full',
                            plan.compliance_rate >= 90 ? 'bg-green-600' :
                            plan.compliance_rate >= 70 ? 'bg-yellow-600' : 'bg-red-600'
                          ]"
                        ></div>
                      </div>
                      <span class="text-sm font-medium text-gray-900">{{ plan.compliance_rate }}%</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Parts Metrics Tab -->
    <div v-if="activeTab === 'parts'" class="space-y-6">
      <!-- Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Inventory Value</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                ${{ formatNumber(partsMetrics.total_inventory_value) }}
              </p>
            </div>
            <div class="p-3 bg-blue-100 rounded-lg">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Parts Used</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ partsMetrics.parts_used }}
              </p>
              <p class="text-xs text-gray-500 mt-1">${{ formatNumber(partsMetrics.parts_cost) }}</p>
            </div>
            <div class="p-3 bg-green-100 rounded-lg">
              <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Low Stock Alerts</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ partsMetrics.low_stock_count }}
              </p>
            </div>
            <div class="p-3 bg-yellow-100 rounded-lg">
              <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Out of Stock</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ partsMetrics.out_of_stock_count }}
              </p>
            </div>
            <div class="p-3 bg-red-100 rounded-lg">
              <svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts and Tables -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Parts Usage Trend -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Parts Usage Trend</h3>
          <canvas ref="partsUsageChart"></canvas>
        </div>

        <!-- Top Parts by Usage -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Top 10 Parts by Usage</h3>
          <div class="space-y-3">
            <div
              v-for="part in partsMetrics.top_parts"
              :key="part.id"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div>
                <p class="text-sm font-medium text-gray-900">{{ part.name }}</p>
                <p class="text-xs text-gray-500">{{ part.part_number }}</p>
              </div>
              <div class="text-right">
                <p class="text-sm font-bold text-gray-900">{{ part.quantity_used }}</p>
                <p class="text-xs text-gray-500">${{ formatNumber(part.total_cost) }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Low Stock Alerts -->
        <div class="bg-white rounded-lg shadow p-6 md:col-span-2">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Low Stock Alerts</h3>
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Part Number</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">On Hand</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reorder Point</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit Cost</th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200">
                <tr v-for="part in partsMetrics.low_stock_parts" :key="part.id">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ part.name }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ part.part_number }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ part.quantity_on_hand }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ part.reorder_point }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">${{ part.unit_cost }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <span
                      :class="[
                        'px-2 py-1 text-xs font-medium rounded-full',
                        part.quantity_on_hand === 0
                          ? 'bg-red-100 text-red-800'
                          : 'bg-yellow-100 text-yellow-800'
                      ]"
                    >
                      {{ part.quantity_on_hand === 0 ? 'Out of Stock' : 'Low Stock' }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Technician Performance Tab -->
    <div v-if="activeTab === 'technicians'" class="space-y-6">
      <!-- Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Active Technicians</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ technicianMetrics.active_count }}
              </p>
            </div>
            <div class="p-3 bg-blue-100 rounded-lg">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Avg WOs/Technician</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ technicianMetrics.avg_wo_per_tech }}
              </p>
            </div>
            <div class="p-3 bg-green-100 rounded-lg">
              <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Avg Completion Time</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ technicianMetrics.avg_completion_time }} hrs
              </p>
            </div>
            <div class="p-3 bg-purple-100 rounded-lg">
              <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Team Efficiency</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                {{ technicianMetrics.team_efficiency }}%
              </p>
            </div>
            <div class="p-3 bg-yellow-100 rounded-lg">
              <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Technician Performance Table -->
      <div class="bg-white rounded-lg shadow p-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">Technician Performance</h3>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Technician</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Completed</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">In Progress</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Time (hrs)</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">On-Time Rate</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Efficiency</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="tech in technicianMetrics.technicians" :key="tech.id">
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center">
                    <div class="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-medium">
                      {{ tech.initials }}
                    </div>
                    <div class="ml-3">
                      <p class="text-sm font-medium text-gray-900">{{ tech.name }}</p>
                      <p class="text-xs text-gray-500">{{ tech.email }}</p>
                    </div>
                  </div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ tech.assigned_count }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">{{ tech.completed_count }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-blue-600">{{ tech.in_progress_count }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ tech.avg_completion_time }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ tech.on_time_rate }}%</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="flex items-center gap-2">
                    <div class="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        :style="{ width: tech.efficiency_score + '%' }"
                        :class="[
                          'h-2 rounded-full',
                          tech.efficiency_score >= 85 ? 'bg-green-600' :
                          tech.efficiency_score >= 70 ? 'bg-yellow-600' : 'bg-red-600'
                        ]"
                      ></div>
                    </div>
                    <span class="text-sm font-medium text-gray-900">{{ tech.efficiency_score }}%</span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Charts -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Workload Distribution -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Workload Distribution</h3>
          <canvas ref="workloadChart"></canvas>
        </div>

        <!-- Completion Rate Comparison -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Completion Rate Comparison</h3>
          <canvas ref="completionRateChart"></canvas>
        </div>
      </div>
    </div>

    <!-- Cost Analysis Tab -->
    <div v-if="activeTab === 'costs'" class="space-y-6">
      <!-- Summary Cards -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Total Maintenance Cost</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                ${{ formatNumber(costMetrics.total_cost) }}
              </p>
            </div>
            <div class="p-3 bg-blue-100 rounded-lg">
              <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Labor Cost</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                ${{ formatNumber(costMetrics.labor_cost) }}
              </p>
              <p class="text-xs text-gray-500 mt-1">{{ costMetrics.labor_percentage }}% of total</p>
            </div>
            <div class="p-3 bg-green-100 rounded-lg">
              <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Parts Cost</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                ${{ formatNumber(costMetrics.parts_cost) }}
              </p>
              <p class="text-xs text-gray-500 mt-1">{{ costMetrics.parts_percentage }}% of total</p>
            </div>
            <div class="p-3 bg-purple-100 rounded-lg">
              <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-600">Cost per Asset</p>
              <p class="text-2xl font-bold text-gray-900 mt-1">
                ${{ formatNumber(costMetrics.cost_per_asset) }}
              </p>
            </div>
            <div class="p-3 bg-yellow-100 rounded-lg">
              <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Cost Trend -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Cost Trend</h3>
          <canvas ref="costTrendChartTab"></canvas>
        </div>

        <!-- Labor vs Parts -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Labor vs Parts Cost</h3>
          <canvas ref="laborVsPartsChart"></canvas>
        </div>

        <!-- Cost by Category -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Cost by Category</h3>
          <canvas ref="costByCategoryChart"></canvas>
        </div>

        <!-- Cost by Asset -->
        <div class="bg-white rounded-lg shadow p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Top 10 Assets by Cost</h3>
          <div class="space-y-3">
            <div
              v-for="asset in costMetrics.top_assets"
              :key="asset.id"
              class="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
            >
              <div>
                <p class="text-sm font-medium text-gray-900">{{ asset.asset_tag }}</p>
                <p class="text-xs text-gray-500">{{ asset.name }}</p>
              </div>
              <div class="text-right">
                <p class="text-sm font-bold text-gray-900">${{ formatNumber(asset.total_cost) }}</p>
                <p class="text-xs text-gray-500">
                  L: ${{ formatNumber(asset.labor_cost) }} | P: ${{ formatNumber(asset.parts_cost) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<script setup>
import { ref, reactive, onMounted, computed, nextTick } from 'vue';
import { router } from '@inertiajs/vue3';
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import Chart from 'chart.js/auto';
import route from '@/Utils/route';

// Props
const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({})
  }
});

// Tabs
const tabs = [
  { id: 'assets', label: 'Asset Metrics' },
  { id: 'work_orders', label: 'Work Orders' },
  { id: 'pm_compliance', label: 'PM Compliance' },
  { id: 'parts', label: 'Parts' },
  { id: 'technicians', label: 'Technicians' },
  { id: 'costs', label: 'Cost Analysis' }
];

const activeTab = ref('assets');

// Filters
const filters = reactive({
  start_date: getDefaultStartDate(),
  end_date: getDefaultEndDate()
});

// Metrics Data
const assetMetrics = ref({
  avg_utilization: 0,
  total_downtime: 0,
  total_maintenance_cost: 0,
  mtbf: 0,
  mttr: 0,
  top_assets_by_cost: []
});

const workOrderMetrics = ref({
  completion_rate: 0,
  avg_completion_time: 0,
  backlog_count: 0,
  overdue_count: 0,
  total_cost: 0
});

const pmMetrics = ref({
  compliance_rate: 0,
  scheduled_count: 0,
  completed_count: 0,
  overdue_count: 0,
  preventive_wo_count: 0,
  reactive_wo_count: 0,
  pm_reactive_ratio: '0:0',
  effectiveness_score: 0,
  plans: []
});

const partsMetrics = ref({
  total_inventory_value: 0,
  parts_used: 0,
  parts_cost: 0,
  low_stock_count: 0,
  out_of_stock_count: 0,
  top_parts: [],
  low_stock_parts: []
});

const technicianMetrics = ref({
  active_count: 0,
  avg_wo_per_tech: 0,
  avg_completion_time: 0,
  team_efficiency: 0,
  technicians: []
});

const costMetrics = ref({
  total_cost: 0,
  labor_cost: 0,
  parts_cost: 0,
  labor_percentage: 0,
  parts_percentage: 0,
  cost_per_asset: 0,
  top_assets: []
});

// Chart refs
const assetUtilizationChart = ref(null);
const assetDowntimeChart = ref(null);
const costTrendChart = ref(null);
const woStatusChart = ref(null);
const woTypeChart = ref(null);
const woCompletionTrendChart = ref(null);
const woPriorityChart = ref(null);
const pmComplianceChart = ref(null);
const partsUsageChart = ref(null);
const workloadChart = ref(null);
const completionRateChart = ref(null);
const costTrendChartTab = ref(null);
const laborVsPartsChart = ref(null);
const costByCategoryChart = ref(null);

// Chart instances
let chartInstances = {};

// Methods
function getDefaultStartDate() {
  const date = new Date();
  date.setMonth(date.getMonth() - 3);
  return date.toISOString().split('T')[0];
}

function getDefaultEndDate() {
  return new Date().toISOString().split('T')[0];
}

function formatNumber(value) {
  if (!value && value !== 0) return '0';
  return parseFloat(value).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
}

async function loadReports() {
  try {
    const response = await fetch(route('maintenance.reports.data', filters));
    const data = await response.json();

    assetMetrics.value = data.asset_metrics;
    workOrderMetrics.value = data.work_order_metrics;
    pmMetrics.value = data.pm_metrics;
    partsMetrics.value = data.parts_metrics;
    technicianMetrics.value = data.technician_metrics;
    costMetrics.value = data.cost_metrics;

    await nextTick();
    renderCharts();
  } catch (error) {
    console.error('Error loading reports:', error);
  }
}

function renderCharts() {
  // Destroy existing charts
  Object.values(chartInstances).forEach(chart => chart?.destroy());
  chartInstances = {};

  // Render all charts
  renderAssetCharts();
  renderWorkOrderCharts();
  renderPmCharts();
  renderPartsCharts();
  renderTechnicianCharts();
  renderCostCharts();
}

function renderAssetCharts() {
  // Asset Utilization Chart
  if (assetUtilizationChart.value) {
    chartInstances.assetUtilization = new Chart(assetUtilizationChart.value, {
      type: 'bar',
      data: {
        labels: assetMetrics.value.utilization_by_category?.map(c => c.name) || [],
        datasets: [{
          label: 'Utilization %',
          data: assetMetrics.value.utilization_by_category?.map(c => c.utilization) || [],
          backgroundColor: 'rgba(59, 130, 246, 0.5)',
          borderColor: 'rgb(59, 130, 246)',
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            max: 100
          }
        }
      }
    });
  }

  // Asset Downtime Chart
  if (assetDowntimeChart.value) {
    chartInstances.assetDowntime = new Chart(assetDowntimeChart.value, {
      type: 'bar',
      data: {
        labels: assetMetrics.value.downtime_by_asset?.map(a => a.asset_tag) || [],
        datasets: [{
          label: 'Downtime (hours)',
          data: assetMetrics.value.downtime_by_asset?.map(a => a.downtime) || [],
          backgroundColor: 'rgba(239, 68, 68, 0.5)',
          borderColor: 'rgb(239, 68, 68)',
          borderWidth: 1
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        indexAxis: 'y'
      }
    });
  }

  // Cost Trend Chart
  if (costTrendChart.value) {
    chartInstances.costTrend = new Chart(costTrendChart.value, {
      type: 'line',
      data: {
        labels: assetMetrics.value.cost_trend?.map(c => c.month) || [],
        datasets: [{
          label: 'Maintenance Cost',
          data: assetMetrics.value.cost_trend?.map(c => c.cost) || [],
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          tension: 0.4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }
}

function renderWorkOrderCharts() {
  // WO Status Chart
  if (woStatusChart.value) {
    chartInstances.woStatus = new Chart(woStatusChart.value, {
      type: 'doughnut',
      data: {
        labels: workOrderMetrics.value.by_status?.map(s => s.status) || [],
        datasets: [{
          data: workOrderMetrics.value.by_status?.map(s => s.count) || [],
          backgroundColor: [
            'rgba(59, 130, 246, 0.5)',
            'rgba(34, 197, 94, 0.5)',
            'rgba(251, 191, 36, 0.5)',
            'rgba(239, 68, 68, 0.5)',
            'rgba(168, 85, 247, 0.5)'
          ]
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }

  // WO Type Chart
  if (woTypeChart.value) {
    chartInstances.woType = new Chart(woTypeChart.value, {
      type: 'pie',
      data: {
        labels: workOrderMetrics.value.by_type?.map(t => t.type) || [],
        datasets: [{
          data: workOrderMetrics.value.by_type?.map(t => t.count) || [],
          backgroundColor: [
            'rgba(59, 130, 246, 0.5)',
            'rgba(34, 197, 94, 0.5)',
            'rgba(251, 191, 36, 0.5)',
            'rgba(239, 68, 68, 0.5)',
            'rgba(168, 85, 247, 0.5)',
            'rgba(107, 114, 128, 0.5)'
          ]
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }

  // Completion Trend Chart
  if (woCompletionTrendChart.value) {
    chartInstances.woCompletionTrend = new Chart(woCompletionTrendChart.value, {
      type: 'line',
      data: {
        labels: workOrderMetrics.value.completion_trend?.map(c => c.week) || [],
        datasets: [{
          label: 'Completed',
          data: workOrderMetrics.value.completion_trend?.map(c => c.completed) || [],
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          tension: 0.4
        }, {
          label: 'Created',
          data: workOrderMetrics.value.completion_trend?.map(c => c.created) || [],
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }

  // Priority Chart
  if (woPriorityChart.value) {
    chartInstances.woPriority = new Chart(woPriorityChart.value, {
      type: 'bar',
      data: {
        labels: workOrderMetrics.value.by_priority?.map(p => p.priority) || [],
        datasets: [{
          label: 'Work Orders',
          data: workOrderMetrics.value.by_priority?.map(p => p.count) || [],
          backgroundColor: [
            'rgba(34, 197, 94, 0.5)',
            'rgba(59, 130, 246, 0.5)',
            'rgba(251, 191, 36, 0.5)',
            'rgba(239, 68, 68, 0.5)'
          ]
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }
}

function renderPmCharts() {
  // PM Compliance Chart
  if (pmComplianceChart.value) {
    chartInstances.pmCompliance = new Chart(pmComplianceChart.value, {
      type: 'line',
      data: {
        labels: pmMetrics.value.compliance_trend?.map(c => c.month) || [],
        datasets: [{
          label: 'Compliance Rate %',
          data: pmMetrics.value.compliance_trend?.map(c => c.rate) || [],
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          tension: 0.4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            max: 100
          }
        }
      }
    });
  }
}

function renderPartsCharts() {
  // Parts Usage Chart
  if (partsUsageChart.value) {
    chartInstances.partsUsage = new Chart(partsUsageChart.value, {
      type: 'line',
      data: {
        labels: partsMetrics.value.usage_trend?.map(u => u.month) || [],
        datasets: [{
          label: 'Parts Cost',
          data: partsMetrics.value.usage_trend?.map(u => u.cost) || [],
          borderColor: 'rgb(168, 85, 247)',
          backgroundColor: 'rgba(168, 85, 247, 0.1)',
          tension: 0.4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }
}

function renderTechnicianCharts() {
  // Workload Distribution Chart
  if (workloadChart.value) {
    chartInstances.workload = new Chart(workloadChart.value, {
      type: 'bar',
      data: {
        labels: technicianMetrics.value.technicians?.map(t => t.name.split(' ')[0]) || [],
        datasets: [{
          label: 'Assigned',
          data: technicianMetrics.value.technicians?.map(t => t.assigned_count) || [],
          backgroundColor: 'rgba(59, 130, 246, 0.5)'
        }, {
          label: 'Completed',
          data: technicianMetrics.value.technicians?.map(t => t.completed_count) || [],
          backgroundColor: 'rgba(34, 197, 94, 0.5)'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }

  // Completion Rate Comparison Chart
  if (completionRateChart.value) {
    chartInstances.completionRate = new Chart(completionRateChart.value, {
      type: 'bar',
      data: {
        labels: technicianMetrics.value.technicians?.map(t => t.name.split(' ')[0]) || [],
        datasets: [{
          label: 'On-Time Rate %',
          data: technicianMetrics.value.technicians?.map(t => t.on_time_rate) || [],
          backgroundColor: 'rgba(34, 197, 94, 0.5)'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            max: 100
          }
        }
      }
    });
  }
}

function renderCostCharts() {
  // Cost Trend Chart (Tab)
  if (costTrendChartTab.value) {
    chartInstances.costTrendTab = new Chart(costTrendChartTab.value, {
      type: 'line',
      data: {
        labels: costMetrics.value.trend?.map(c => c.month) || [],
        datasets: [{
          label: 'Total Cost',
          data: costMetrics.value.trend?.map(c => c.total) || [],
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }

  // Labor vs Parts Chart
  if (laborVsPartsChart.value) {
    chartInstances.laborVsParts = new Chart(laborVsPartsChart.value, {
      type: 'doughnut',
      data: {
        labels: ['Labor Cost', 'Parts Cost'],
        datasets: [{
          data: [costMetrics.value.labor_cost, costMetrics.value.parts_cost],
          backgroundColor: [
            'rgba(34, 197, 94, 0.5)',
            'rgba(168, 85, 247, 0.5)'
          ]
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }

  // Cost by Category Chart
  if (costByCategoryChart.value) {
    chartInstances.costByCategory = new Chart(costByCategoryChart.value, {
      type: 'bar',
      data: {
        labels: costMetrics.value.by_category?.map(c => c.category) || [],
        datasets: [{
          label: 'Total Cost',
          data: costMetrics.value.by_category?.map(c => c.cost) || [],
          backgroundColor: 'rgba(59, 130, 246, 0.5)'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false
      }
    });
  }
}

function exportReport() {
  window.open(route('maintenance.reports.export', filters), '_blank');
}

// Lifecycle
onMounted(() => {
  loadReports();
});
</script>

<style scoped>
canvas {
  max-height: 300px;
}
</style>
